import classNames from "classnames";
import { useEffect, useState } from "react";
import { Button } from "@/components";
import { useMarkIssueAsRead } from "../../_hooks/useMarkIssueAsRead";
import { Issue } from "../../_types/issues.types";
import { IssueDetailsModal } from "../issue-details-modal";
import { IssueListItem } from "../issue-list-item";
import styles from "./IssuesList.module.scss";

type IssuesListProps = {
  issues: Array<Issue>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
};

export const IssuesList = ({
  issues,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: IssuesListProps) => {
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const markAsReadMutation = useMarkIssueAsRead();

  useEffect(() => {
    if (!selectedIssue) return;

    const nextSelectedIssue = issues.find((issue) => issue.id === selectedIssue.id) ?? null;

    if (nextSelectedIssue) {
      setSelectedIssue(nextSelectedIssue);
    }
  }, [issues, selectedIssue]);

  const handleOpenDetails = (issue: Issue) => {
    const nextIssue = issue.read ? issue : { ...issue, read: true };

    setSelectedIssue(nextIssue);

    if (!issue.read) {
      markAsReadMutation.mutate(issue.id);
    }
  };

  if (isLoading) {
    return (
      <div className={styles["comp__state"]}>
        <div className={styles["comp__spinner"]} />
        <div>Loading issues...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles["comp__state"]}>
        <div>Failed to load issues.</div>
        <Button size="small" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (issues.length === 0) {
    return <div className={styles["comp__state"]}>No issues found.</div>;
  }

  return (
    <>
      <div className={styles["comp"]} aria-busy={isFetching}>
        <div className={styles["comp__table-header"]}>
          <div
            className={classNames(styles["comp__column"], styles["comp__column--person"])}
          >
            Reporter
          </div>
          <div
            className={classNames(styles["comp__column"], styles["comp__column--category"])}
          >
            Type
          </div>
          <div
            className={classNames(styles["comp__column"], styles["comp__column--model"])}
          >
            Model
          </div>
          <div
            className={classNames(styles["comp__column"], styles["comp__column--content"])}
          >
            Description
          </div>
          <div
            className={classNames(styles["comp__column"], styles["comp__column--date"])}
          >
            Date
          </div>
        </div>

        <div className={styles["comp__list"]}>
          {issues.map((issue) => (
            <IssueListItem key={issue.id} issue={issue} onClick={() => handleOpenDetails(issue)} />
          ))}
        </div>
      </div>

      <IssueDetailsModal
        issue={selectedIssue}
        isOpen={selectedIssue !== null}
        isUpdatingReadStatus={markAsReadMutation.isPending}
        onClose={() => setSelectedIssue(null)}
      />
    </>
  );
};
